import { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle, CardDescription, CardFooter } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';
import { 
  Crown, 
  Check, 
  Zap, 
  BarChart3, 
  Clock, 
  Star,
  Sparkles,
  Shield,
  Loader2,
  Video,
  Users,
  EyeOff,
  Palette
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const SubscriptionPage = () => {
  const { user, token } = useAuth();
  const [packages, setPackages] = useState({});
  const [subscriptionStatus, setSubscriptionStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [checkoutLoading, setCheckoutLoading] = useState(null);

  useEffect(() => {
    fetchData();
    checkPaymentReturn();
  }, [token]);

  const fetchData = async () => {
    try {
      // Fetch packages
      const packagesRes = await fetch(`${API_URL}/api/subscription/packages`);
      const packagesData = await packagesRes.json();
      setPackages(packagesData.packages || {});

      // Fetch subscription status if logged in
      if (token) {
        const statusRes = await fetch(`${API_URL}/api/subscription/status`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        const statusData = await statusRes.json();
        setSubscriptionStatus(statusData);
      }
    } catch (err) {
      console.error('Error fetching subscription data:', err);
    } finally {
      setLoading(false);
    }
  };

  const checkPaymentReturn = async () => {
    const urlParams = new URLSearchParams(window.location.search);
    const sessionId = urlParams.get('session_id');
    
    if (sessionId && token) {
      toast.info('Checking payment status...');
      await pollPaymentStatus(sessionId);
      // Clear URL params
      window.history.replaceState({}, document.title, window.location.pathname);
    }
  };

  const pollPaymentStatus = async (sessionId, attempts = 0) => {
    const maxAttempts = 5;
    const pollInterval = 2000;

    if (attempts >= maxAttempts) {
      toast.error('Payment verification timed out. Please check your email for confirmation.');
      return;
    }

    try {
      const response = await fetch(`${API_URL}/api/subscription/checkout/status/${sessionId}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      const data = await response.json();
      if (!response.ok) throw new Error(data.detail || 'Failed to check payment status');
      
      if (data.payment_status === 'paid') {
        toast.success('Payment successful! Welcome to Trader Titans Pro!');
        fetchData(); // Refresh subscription status
        return;
      } else if (data.status === 'expired') {
        toast.error('Payment session expired. Please try again.');
        return;
      }
      
      // Continue polling
      setTimeout(() => pollPaymentStatus(sessionId, attempts + 1), pollInterval);
    } catch (error) {
      console.error('Error checking payment status:', error);
      toast.error(error.message || 'Error checking payment status.');
    }
  };

  const handleSubscribe = async (packageId) => {
    if (!token) {
      toast.error('Please sign in to subscribe');
      return;
    }

    setCheckoutLoading(packageId);
    
    try {
      const response = await fetch(`${API_URL}/api/subscription/checkout`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          package_id: packageId,
          origin_url: window.location.origin
        })
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || 'Failed to create checkout session');
      }

      const data = await response.json();
      
      // Redirect to Stripe Checkout
      if (data.url) {
        window.location.href = data.url;
      }
    } catch (error) {
      console.error('Checkout error:', error);
      toast.error(error.message);
    } finally {
      setCheckoutLoading(null);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const isPro = subscriptionStatus?.is_pro;

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-950 flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-emerald-400" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-950 py-8 px-4" data-testid="subscription-page">
      <div className="max-w-5xl mx-auto">
        {/* Header */}
        <div className="text-center mb-12">
          <div className="inline-flex items-center gap-2 mb-4">
            <Crown className="h-8 w-8 text-yellow-400" />
            <h1 className="text-3xl sm:text-4xl font-bold text-white">Trader Titans Pro</h1>
          </div>
          <p className="text-slate-400 text-lg max-w-2xl mx-auto">
            Unlock advanced features and gain a competitive edge in your fantasy trading journey
          </p>
        </div>

        {/* Current Subscription Status */}
        {isPro && (
          <Card className="mb-8 bg-gradient-to-r from-yellow-500/10 to-amber-500/10 border-yellow-500/30">
            <CardContent className="py-6">
              <div className="flex items-center gap-4">
                <div className="p-3 bg-yellow-500/20 rounded-full">
                  <Crown className="h-6 w-6 text-yellow-400" />
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-white flex items-center gap-2">
                    You're a Pro Member!
                    <Badge className="bg-yellow-500/20 text-yellow-400">Active</Badge>
                  </h3>
                  <p className="text-slate-400">
                    Member since {formatDate(subscriptionStatus?.pro_since)} • 
                    Renews {formatDate(subscriptionStatus?.pro_expires)}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Pricing Cards */}
        <div className="grid md:grid-cols-2 gap-6 mb-12">
          {/* Monthly Plan */}
          <Card className="bg-slate-900/50 border-slate-800 hover:border-emerald-500/50 transition-all">
            <CardHeader>
              <CardTitle className="text-xl text-white">Monthly</CardTitle>
              <CardDescription className="text-slate-400">
                Perfect for trying out Pro features
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="mb-6">
                <span className="text-4xl font-bold text-white">$9.99</span>
                <span className="text-slate-400">/month</span>
              </div>
              
              <ul className="space-y-3 mb-6">
                {packages.pro_monthly?.features?.map((feature, idx) => (
                  <li key={idx} className="flex items-center gap-2 text-slate-300">
                    <Check className="h-4 w-4 text-emerald-400 flex-shrink-0" />
                    {feature}
                  </li>
                ))}
              </ul>
            </CardContent>
            <CardFooter>
              <Button 
                className="w-full bg-emerald-600 hover:bg-emerald-500"
                onClick={() => handleSubscribe('pro_monthly')}
                disabled={isPro || checkoutLoading === 'pro_monthly'}
                data-testid="subscribe-monthly-btn"
              >
                {checkoutLoading === 'pro_monthly' ? (
                  <><Loader2 className="h-4 w-4 animate-spin mr-2" /> Processing...</>
                ) : isPro ? (
                  'Currently Active'
                ) : (
                  'Subscribe Monthly'
                )}
              </Button>
            </CardFooter>
          </Card>

          {/* Yearly Plan */}
          <Card className="bg-slate-900/50 border-yellow-500/50 relative overflow-hidden">
            <div className="absolute top-4 right-4">
              <Badge className="bg-yellow-500 text-black font-semibold">
                Save 17%
              </Badge>
            </div>
            <CardHeader>
              <CardTitle className="text-xl text-white flex items-center gap-2">
                Annual
                <Sparkles className="h-4 w-4 text-yellow-400" />
              </CardTitle>
              <CardDescription className="text-slate-400">
                Best value - 2 months free
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="mb-6">
                <span className="text-4xl font-bold text-white">$99.99</span>
                <span className="text-slate-400">/year</span>
                <p className="text-sm text-emerald-400 mt-1">That's just $8.33/month</p>
              </div>
              
              <ul className="space-y-3 mb-6">
                {packages.pro_yearly?.features?.map((feature, idx) => (
                  <li key={idx} className="flex items-center gap-2 text-slate-300">
                    <Check className="h-4 w-4 text-yellow-400 flex-shrink-0" />
                    {feature}
                  </li>
                ))}
              </ul>
            </CardContent>
            <CardFooter>
              <Button 
                className="w-full bg-yellow-500 hover:bg-yellow-400 text-black font-semibold"
                onClick={() => handleSubscribe('pro_yearly')}
                disabled={isPro || checkoutLoading === 'pro_yearly'}
                data-testid="subscribe-yearly-btn"
              >
                {checkoutLoading === 'pro_yearly' ? (
                  <><Loader2 className="h-4 w-4 animate-spin mr-2" /> Processing...</>
                ) : isPro ? (
                  'Currently Active'
                ) : (
                  'Subscribe Annually'
                )}
              </Button>
            </CardFooter>
          </Card>
        </div>

        {/* Features Grid */}
        <div className="mb-12">
          <h2 className="text-2xl font-bold text-white text-center mb-8">
            What's Included in Pro
          </h2>
          
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            <FeatureCard 
              icon={<Video className="h-6 w-6" />}
              title="Creator Hub"
              description="Streaming widgets, weekly content packs, and trade alert webhooks for your audience"
              color="emerald"
            />
            <FeatureCard 
              icon={<Sparkles className="h-6 w-6" />}
              title="AI Script Generator"
              description="Generate YouTube scripts with live market data, strategy picks, and voiceover-ready formatting"
              color="purple"
            />
            <FeatureCard 
              icon={<Users className="h-6 w-6" />}
              title="Unlimited Mini Leagues"
              description="Create unlimited leagues with up to 500 players each (free tier: 1 league, 20 players)"
              color="blue"
            />
            <FeatureCard 
              icon={<EyeOff className="h-6 w-6" />}
              title="Portfolio Privacy"
              description="Hide your holdings from other players — only your stats and cosmetics are visible"
              color="pink"
            />
            <FeatureCard 
              icon={<Palette className="h-6 w-6" />}
              title="Pro Cosmetics"
              description="Exclusive themes, badges, card styles, and avatar frames to stand out on leaderboards"
              color="amber"
            />
            <FeatureCard 
              icon={<Star className="h-6 w-6" />}
              title="Priority Support & Early Access"
              description="Faster support responses and first access to new features before they go live"
              color="yellow"
            />
          </div>
        </div>

        {/* FAQ */}
        <div className="text-center">
          <p className="text-slate-400">
            Questions? Contact us at{' '}
            <a href="mailto:support@tradertitans.com" className="text-emerald-400 hover:underline">
              support@tradertitans.com
            </a>
          </p>
        </div>
      </div>
    </div>
  );
};

const FeatureCard = ({ icon, title, description, color }) => {
  const colorClasses = {
    emerald: 'bg-emerald-500/10 text-emerald-400 border-emerald-500/30',
    blue: 'bg-blue-500/10 text-blue-400 border-blue-500/30',
    purple: 'bg-purple-500/10 text-purple-400 border-purple-500/30',
    yellow: 'bg-yellow-500/10 text-yellow-400 border-yellow-500/30',
    pink: 'bg-pink-500/10 text-pink-400 border-pink-500/30',
    amber: 'bg-amber-500/10 text-amber-400 border-amber-500/30'
  };

  return (
    <Card className={`${colorClasses[color]} border`}>
      <CardContent className="pt-6">
        <div className="mb-3">{icon}</div>
        <h3 className="font-semibold text-white mb-1">{title}</h3>
        <p className="text-sm text-slate-400">{description}</p>
      </CardContent>
    </Card>
  );
};

export default SubscriptionPage;
